/* begin general import */
import { Add16, Edit24, TrashCan16 } from "@carbon/icons-react";
import { ColumnProps } from "antd/lib/table";
import { UserManual, UserManualFilter } from "models/UserManual";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  LayoutCell,
  LayoutHeader,
  OneLineText,
  Pagination,
  StandardTable,
} from "react3l-ui-library";
import { userManualRepository } from "repositories/user-manual-repository";
import { detailService } from "services/page-services/detail-service";
import { queryStringService } from "services/page-services/query-string-service";
import { filterService } from "services/page-services/filter-service";
import { Site } from "models/Site";
import { AppUser } from "models/AppUser";
import { Moment } from "moment";
import { formatDate } from "helpers/date-time";
import UserManualDetailDrawer from "../UserManualDetail/UserManualDetailDrawer";
import { FileTemplate } from "models/FileTemplate";
import { Popconfirm, Tooltip } from "antd";
import { listService } from "services/page-services/list-service";
import { userManualMasterService } from "./master-service";
import { authorizationService } from "services/common-services/authorization-service";
import { API_USER_MANUAL_PREFIX } from "config/api-consts";
import { USER_MANUAL_ROUTE } from "config/route-consts";

function UserManualMaster() {
  const [translate] = useTranslation();
  const { validAction } = authorizationService.useAuthorizedAction(
    USER_MANUAL_ROUTE.split("/")[USER_MANUAL_ROUTE.split("/").length - 1],
    API_USER_MANUAL_PREFIX
  );
  const [modelFilter, dispatch] = queryStringService.useQueryString(
    UserManualFilter,
    { currentPage: 0, pageSize: 10 }
  );

  const { value: filter, handleChangeAllFilter } = filterService.useFilter(
    modelFilter,
    dispatch
  );

  const { list, count, loadingList, handleLoadList } = listService.useList<
    UserManual,
    UserManualFilter
  >(userManualRepository.list, userManualRepository.count, filter, dispatch);

  const { handleTableChange, handlePagination } =
    userManualMasterService.useTable(filter, handleChangeAllFilter);

  const { handleAction } = listService.useRowSelection(
    userManualRepository.delete,
    userManualRepository.bulkDelete,
    null,
    null,
    null,
    handleLoadList
  );

  const {
    model,
    isOpenDetailModal,
    handleOpenDetailModal,
    handleCloseDetailModal,
    handleSaveModel,
    loadingModel,
    handleChangeSingleField,
    handleChangeSelectField,
  } = detailService.useDetailModal(
    UserManual,
    userManualRepository.get,
    userManualRepository.save,
    handleLoadList
  );

  const handleOpenLink = (link: any) => () => {
    window.open(link.toString(), "_self");
  };

  const columns: ColumnProps<UserManual>[] = useMemo(
    () => [
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("general.actions.orderNumber")}
            />
          );
        },
        width: 80,
        key: "id",
        dataIndex: "id",
        align: "center",
        render(...params: [string, UserManual, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("userManuals.fullName")}
            />
          );
        },
        key: "fullName",
        dataIndex: "fullName",
        render(...params: [string, UserManual, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
        width: 500,
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("userManuals.site")}
            />
          );
        },
        width: 150,
        key: "site",
        dataIndex: "site",
        render(...params: [Site, UserManual, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]?.name} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("userManuals.appUser")}
            />
          );
        },
        key: "appUser",
        dataIndex: "appUser",
        render(...params: [AppUser, UserManual, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]?.displayName} />
            </LayoutCell>
          );
        },
        width: 200,
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("userManuals.createdAt")}
            />
          );
        },
        width: 150,
        key: "createdAt",
        dataIndex: "createdAt",
        render(...params: [Moment, UserManual, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={formatDate(params[0])} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("userManuals.url")}
            />
          );
        },
        key: "url",
        dataIndex: "file",
        render(...params: [FileTemplate, UserManual, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <div
                style={{ cursor: "pointer" }}
                className="text-link"
                onClick={handleOpenLink(params[0]?.url)}
              >
                {params[0]?.url}
              </div>
            </LayoutCell>
          );
        },
      },
      {
        title: translate("general.actions.label"),
        key: "action",
        dataIndex: "id",
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, userManual: UserManual) {
          return (
            <div className="d-flex justify-content-center button-action-table missions-dxg">
              {validAction("update") && (
                <Tooltip title={translate("general.actions.edit")}>
                  <div className="enable-dxg">
                    <Edit24
                      className="btn-missions-blue"
                      onClick={() => {
                        handleOpenDetailModal(id);
                      }}
                    />
                  </div>
                </Tooltip>
              )}
              {validAction("delete") && (
                <div className="enable-dxg ml-1">
                  <Popconfirm
                    title={translate("general.deletes.content")}
                    onConfirm={() => handleAction(userManual)}
                    okText={translate("general.actions.accept")}
                    cancelText={translate("general.actions.deny")}
                  >
                    <TrashCan16 className="btn-missions-red" />
                  </Popconfirm>
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [handleOpenDetailModal, translate, validAction, handleAction]
  );

  return (
    <>
      <div className="page-content page-content-dxg" id="page-content-dxg-id">
        <div className="info-component ml-4 mb-3">
          <span>{translate("menu.userManual")}</span>
          <span> &gt; </span>
          <span className="info-primary">
            {translate("userManuals.master.subHeader")}
          </span>
        </div>

        <div className="page page-master m-t--lg m-b--xxs">
          <div className="pb-3 ml-3 pt-4">
            <h5 className="font-bold page-title">
              {translate("userManuals.master.subHeader")}
            </h5>
          </div>

          <div className="page-master__content">
            <div className="page-master__filter-action d-flex align-items-center justify-content-end pr-3">
              {validAction("create") && (
                <Button
                  type="primary"
                  className="btn--lg btn-orange-wIcon ml-3"
                  icon={<Add16 />}
                  onClick={() => handleOpenDetailModal(null)}
                >
                  {translate("general.actions.create")}
                </Button>
              )}
            </div>
            <div className="page-master__content-table">
              <StandardTable
                rowKey={"id"}
                columns={columns}
                dataSource={list}
                isDragable={true}
                tableSize={"md"}
                scroll={{ y: 500, x: 'max-content' }}
                bordered
                loading={loadingList}
                onChange={handleTableChange}
              />
              <Pagination
                skip={filter?.currentPage}
                take={filter?.pageSize}
                total={count}
                onChange={handlePagination}
              />
            </div>
          </div>
        </div>
      </div>
      <UserManualDetailDrawer
        model={model}
        visible={isOpenDetailModal}
        handleSave={handleSaveModel}
        handleCancel={handleCloseDetailModal}
        handleChangeSingleField={handleChangeSingleField}
        handleChangeSelectField={handleChangeSelectField}
        loading={loadingModel}
        visibleFooter={true}
      />
    </>
  );
}
export default UserManualMaster;
